import { RotateCcw, Search } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Progress } from '@nucleus-commerce/ui/components/progress';
import { toast } from '@nucleus-commerce/ui/components/sonner';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { PageContent } from '../../layout/page-content';
import { PageHeader } from '../../layout/page-header';
import { tourSummaryKey, tourTitleKey } from '../registry';
import { useTour } from '../tour-context';
import type { TourDefinition } from '../types';

import { HelpTourCard } from './help-tour-card';

export interface HelpCenterTargets {
  progress: string;
  search: string;
  groups: string;
  restart: string;
}

export interface HelpCenterProps {
  /** Group keys in display order; copy at `groups.<key>` in the tours namespace. */
  groups: readonly string[];
  /** `data-tour` values of the page's own tour. */
  targets: HelpCenterTargets;
  /** `?tour=<id>` from the URL: started once, then cleared through `onDeepLinkHandled`. */
  deepLinkTourId?: string | undefined;
  onDeepLinkHandled?: () => Promise<unknown>;
  /** Shown above the groups, e.g. the admin's "your store isn't live yet" callout. */
  callout?: ReactNode;
}

/**
 * The Help and tours page: every tour this person can take, grouped, with status, search, an
 * "only not taken" filter and Start. Shared by the admin and platform apps; each passes its
 * groups and its own tour targets.
 */
export function HelpCenter({
  groups,
  targets,
  deepLinkTourId,
  onDeepLinkHandled,
  callout,
}: HelpCenterProps) {
  const tour = useTour();
  const { t } = useTranslation(tour.namespace);
  const searchId = useId();
  const switchId = useId();
  const [query, setQuery] = useState('');
  const [unseenOnly, setUnseenOnly] = useState(false);
  const [starting, setStarting] = useState<string | null>(null);
  const handledLink = useRef<string | null>(null);

  const available = useMemo(
    () => tour.registry.list.filter((definition) => tour.isAvailable(definition)),
    [tour],
  );
  const taken = available.filter((definition) => {
    const status = tour.statusOf(definition);
    return status === 'done' || status === 'updated';
  }).length;

  const needle = query.trim().toLowerCase();
  const visible = available.filter((definition) => {
    if (unseenOnly) {
      const status = tour.statusOf(definition);
      if (status === 'done') return false;
    }
    if (!needle) return true;
    const text = `${t(tourTitleKey(definition.id), tour.values)} ${t(tourSummaryKey(definition.id))}`;
    return text.toLowerCase().includes(needle);
  });
  const grouped = groups
    .map((group) => ({ group, tours: visible.filter((definition) => definition.group === group) }))
    .filter((entry) => entry.tours.length > 0);

  const start = async (definition: TourDefinition, source: 'help-center' | 'link') => {
    setStarting(definition.id);
    const ok = await tour.start(definition.id, { source });
    setStarting(null);
    if (!ok) toast.error(t('page.cantStart'));
  };

  const { start: startTour, registry, isAvailable } = tour;
  useEffect(() => {
    if (!deepLinkTourId || handledLink.current === deepLinkTourId) return;
    const definition = registry.get(deepLinkTourId);
    if (!definition || !isAvailable(definition)) return;
    handledLink.current = deepLinkTourId;
    void (async () => {
      await onDeepLinkHandled?.();
      const ok = await startTour(definition.id, { source: 'link' });
      if (!ok) toast.error(t('page.cantStart'));
    })();
  }, [deepLinkTourId, isAvailable, onDeepLinkHandled, registry, startTour, t]);

  const welcome = registry.get(tour.welcomeTourId);

  return (
    <PageContent className="flex flex-col gap-6">
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        actions={
          welcome && isAvailable(welcome) ? (
            <Button
              type="button"
              variant="outline"
              data-tour={targets.restart}
              onClick={() => void start(welcome, 'help-center')}
            >
              <RotateCcw aria-hidden />
              {t('page.restartWelcome')}
            </Button>
          ) : null
        }
      />
      <div
        className="bg-card flex flex-col gap-4 rounded-xl border p-4 md:flex-row md:items-center md:justify-between"
        data-tour={targets.progress}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <p className="text-sm font-medium" id={`${searchId}-progress`}>
            {t('page.progress', { done: taken, total: available.length })}
          </p>
          <Progress
            value={available.length === 0 ? 0 : (taken / available.length) * 100}
            aria-labelledby={`${searchId}-progress`}
            className="max-w-md"
          />
          {tour.progress.readOnly ? (
            <p className="text-muted-foreground text-xs">{t('page.readOnly')}</p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Switch id={switchId} checked={unseenOnly} onCheckedChange={setUnseenOnly} />
          <Label htmlFor={switchId} className="text-sm font-normal">
            {t('page.unseenOnly')}
          </Label>
        </div>
      </div>
      <div className="relative max-w-sm" data-tour={targets.search}>
        <Search
          aria-hidden
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
        />
        <Input
          id={searchId}
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label={t('page.search')}
          placeholder={t('page.searchPlaceholder')}
          className="pl-8"
        />
      </div>
      {callout}
      {grouped.length === 0 ? (
        <p className="text-muted-foreground text-sm" role="status">
          {needle ? t('page.noMatches', { query: query.trim() }) : t('page.allTaken')}
        </p>
      ) : (
        grouped.map(({ group, tours }, index) => (
          <section
            key={group}
            aria-labelledby={`help-group-${group}`}
            className="flex flex-col gap-3"
            data-tour={index === 0 ? targets.groups : undefined}
          >
            <h2 id={`help-group-${group}`} className="text-base font-semibold">
              {t(`groups.${group}`)}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {tours.map((definition) => (
                <HelpTourCard
                  key={definition.id}
                  definition={definition}
                  starting={starting === definition.id}
                  onStart={(picked) => void start(picked, 'help-center')}
                />
              ))}
            </div>
          </section>
        ))
      )}
    </PageContent>
  );
}
